import React, { useState } from 'react'
import { styled } from 'styled-components'

import { desktopVw, mobileVw } from '../../styles/utils'

// Components
import Button from '../Button'
import { Input, Select } from '../FormElements'
import { accountStyleCTAButton } from './AccountStyle'
import { useTranslation } from 'next-i18next'
import { validateZipCode } from '@src/quince-site-common/src/hooks/zipcode/utils/zipcode.utils'
import { validatePhone } from '../../utils/validation'
import { capPhoneInputLength } from '../../utils/phone.utils'
import { ADDRESS_FORM_FIELDS } from '@src/quince-components/src/atoms/reviews/createReview/constants/constant'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { getAddressFieldMaxLength } from '@src/quince-site-common/src/services/addressService/address-field-limits.utils'
import GooglePlacesAddressLine1, {
  GOOGLE_PLACES_INTEGRATION_CONTROLLED,
} from './GooglePlacesAddressLine1'
import styles from './addressForm.module.scss'

const StyledContainer = styled.div`
  margin-top: ${mobileVw(16)};
  &.container__half {
    display: flex;
  }
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-top: ${desktopVw(16)};
  }
`

const StyledSelect = styled(Select)`
  width: 100%;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-right: ${desktopVw(16)};
  }
  &.styled_input__error {
    select {
      border: 0.5px solid #ff0000;
    }
  }
  &.styled_input__value {
    select {
      color: ${props => props.theme.colors.primary};
    }
  }
  select {
    border: 0.5px solid ${props => props.theme.colors.silver};
    font-size: ${mobileVw(13)};
    line-height: ${mobileVw(15)};
    padding: 0 ${mobileVw(16)};
    height: ${mobileVw(44)};
    @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
      font-size: ${desktopVw(13)};
      line-height: ${desktopVw(15)};
      padding: 0 ${desktopVw(16)};
      height: ${desktopVw(44)};
    }
    + svg {
      right: ${mobileVw(18)};
      @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
        right: ${desktopVw(18)};
      }
    }
  }
`
const StyledInputWrapper = styled.div`
  width: ${props => (props.halfWidth ? '50%' : '100%')};
  & .styled_input__error {
    color: #ff0000;
    text-transform: none;
  }
  &:last-of-type {
    margin: 0;
  }
  margin-right: ${mobileVw(16)};
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-right: ${desktopVw(16)};
  }
`
const StyledInput = styled(Input)`
  border: 0.5px solid ${props => props.theme.colors.silver};
  &.styled_input__error {
    border: 0.5px solid #ff0000;
  }
  &::placeholder {
    opacity: 0;
  }
  font-size: ${mobileVw(13)};
  line-height: ${mobileVw(15)};
  padding: ${mobileVw(16)} ${mobileVw(16)} 0;
  height: ${mobileVw(44)};

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    font-size: ${desktopVw(13)};
    line-height: ${desktopVw(15)};
    padding: ${desktopVw(16)} ${desktopVw(16)} 0;
    height: ${desktopVw(44)};
  }
`
const FormWrapper = styled.form``

const StyledButton = styled(Button)`
  ${accountStyleCTAButton}
  margin-top: 20px;
  height: 34px;
  width: 167px;
  padding: 0;

  span {
    font-size: ${mobileVw(10)};
    line-height: ${mobileVw(9.6)};
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    height: ${desktopVw(34)};
    width: ${desktopVw(164)};
    margin-top: ${desktopVw(20)};

    span {
      font-size: ${desktopVw(9)};
      line-height: ${desktopVw(15)};
    }
  }
`

const getInputConfig = fields => {
  const result = []
  for (let i = 0; i < fields.length; ) {
    const ans = []
    if (fields[i].width) {
      result.push([fields[i]])
      i += 1
    } else {
      while (!fields[i].width) {
        ans.push(fields[i])
        i += 1
      }
      result.push(ans)
    }
  }

  return result
}

const AddressEditForm = ({
  formData,
  setFormData,
  addAddressForm,
  editOrderLoading,
  validateAddress,
  stateOptions = [],
  countryCode = 'us',
}) => {
  // eslint-disable-next-line i18next/no-literal-string
  const { t } = useTranslation('common')
  const [errors, setErrors] = useState({})
  const [addressData, setAddressData] = useState(formData)
  const [addressEditStateError, setAddressEditStateError] = useState(false)
  const localeConfig = useStatsigDynamicLocaleConfig()

  const handlePhoneInput = e => {
    const stripped = e.target.value.replace(/[^\d+]/g, '')
    // Cap typed digits at the locale's max national digit count (UK 11, US/CA 10)
    // so a user can't enter an over-long number (e.g. 9993345456343423432).
    const value = capPhoneInputLength(stripped, localeConfig?.phone)
    setAddressData({
      ...addressData,
      [e.target.name]: value,
    })
  }

  const handleFormData = e => {
    setAddressData({
      ...addressData,
      [e.target.name]: e.target.value,
    })
  }

  const validate = () => {
    let errors = {}

    const ErrorsObject = addAddressForm.fields.reduce((acc, val) => {
      if (
        val.name == ADDRESS_FORM_FIELDS.zip.key &&
        !validateZipCode(addressData[ADDRESS_FORM_FIELDS.zip.key], localeConfig)
      ) {
        acc[val.name] = {
          message: `${t('enter_a_valid', { msg: val.label })}`,
        }
      } else if (
        val.name == ADDRESS_FORM_FIELDS.phone.key &&
        !validatePhone(addressData[ADDRESS_FORM_FIELDS.phone.key], localeConfig)
      ) {
        acc[val.name] = {
          message: `${t('enter_a_valid', { msg: val.label })}`,
        }
      } else if (val.required !== false) {
        acc[val.name] = {
          message: `${t('enter_a', { msg: val.label })}`,
        }
      }
      return acc
    }, {})
    Object.keys(ErrorsObject).forEach(it => {
      if (it == ADDRESS_FORM_FIELDS.zip.key && !validateZipCode(addressData[it], localeConfig)) {
        errors = { ...errors, [it]: ErrorsObject[it] }
      } else if (it == ADDRESS_FORM_FIELDS.phone.key && !validatePhone(addressData[it], localeConfig)) {
        errors = { ...errors, [it]: ErrorsObject[it] }
      } else if (!addressData[it]) {
        errors = { ...errors, [it]: ErrorsObject[it] }
      }
    })
    setErrors(errors)
    if (Object.keys(errors).length > 0) {
      return false
    }

    return true
  }

  const handleSaveData = () => {
    if (!validate()) return

    if (!validateAddress?.(addressData)) {
      setAddressEditStateError(true)
      return
    }

    setAddressEditStateError(false)
    setFormData(addressData)
  }

  const inputForm = getInputConfig(addAddressForm.fields)

  return (
    <>
      <FormWrapper>
        {inputForm.map((fields, index) => (
          <StyledContainer className={!fields[0]?.width && 'container__half'} key={`formElement-${index}`}>
            {fields.map(f => {
              const contentfulSelectClass = errors[f.name]
                ? `styled_input__error`
                : addressData[f.name]
                  ? // eslint-disable-next-line i18next/no-literal-string
                    'styled_input__value'
                  : ''
              switch (f.__typename) {
                case 'ContentfulInput':
                  return (
                    <React.Fragment key={f.id}>
                      {f.type === 'text' && (
                        <StyledInputWrapper halfWidth={!f.width}>
                          {f.name === 'address1' ? (
                            <GooglePlacesAddressLine1
                              integration={GOOGLE_PLACES_INTEGRATION_CONTROLLED}
                              InputComponent={StyledInput}
                              name={f.name}
                              placeholder={f.placeholder}
                              ariaLabel={f.label}
                              value={addressData?.[f.name]}
                              errors={errors}
                              required={f.required !== false}
                              // eslint-disable-next-line i18next/no-literal-string
                              errorClassName="styled_input__error"
                              onChange={handleFormData}
                              className={errors[f.name] ? 'styled_input__error' : ''}
                              maxLength={getAddressFieldMaxLength(f.name)}
                              stateOptions={stateOptions}
                              countryCode={countryCode}
                              applyAutofillFields={fields => {
                                setAddressData(prev => ({ ...prev, ...fields }))
                              }}
                            />
                          ) : (
                            <StyledInput
                              placeholder={f.placeholder}
                              aria-label={f.label}
                              name={f.name}
                              defaultValue={addressData?.[f.name]}
                              value={addressData?.[f.name]}
                              type={f.name === ADDRESS_FORM_FIELDS.phone.key ? 'tel' : f.type}
                              errors={errors}
                              required={f.required !== false}
                              // eslint-disable-next-line i18next/no-literal-string
                              errorClassName="styled_input__error"
                              onChange={f.name === ADDRESS_FORM_FIELDS.phone.key ? handlePhoneInput : handleFormData}
                              className={errors[f.name] ? 'styled_input__error' : ''}
                              maxLength={getAddressFieldMaxLength(f.name)}
                            />
                          )}
                        </StyledInputWrapper>
                      )}
                    </React.Fragment>
                  )

                case 'ContentfulSelect':
                  return (
                    <StyledInputWrapper halfWidth={!f.width} key={f.id}>
                      <StyledSelect
                        label={f.label}
                        name={f.name}
                        options={f.options}
                        defaultValue={addressData?.[f.name]}
                        errors={errors}
                        // eslint-disable-next-line i18next/no-literal-string
                        errorClassName="styled_input__error"
                        onChange={handleFormData}
                        value={addressData?.[f.name]}
                        className={contentfulSelectClass}
                      />
                    </StyledInputWrapper>
                  )

                default:
                  return null
              }
            })}
          </StyledContainer>
        ))}
        {addressEditStateError ? (
          <div className={styles.errorMessageWrapper}>{t('address_edit_state_error')}</div>
        ) : null}
        <div>
          <StyledButton
            onClick={handleSaveData}
            // @i18n-ignore
            type="button"
            loading={editOrderLoading}
            className={addressEditStateError ? styles.submitButton__withError : styles.submitButton}
          >
            {/* eslint-disable-next-line i18next/no-literal-string */}
            {t('submit_v2')}
          </StyledButton>
        </div>
      </FormWrapper>
    </>
  )
}

export default AddressEditForm
